<template>
    <Modal animation-name="up" class="subscribe-modal" :is-open="modalStore.Subscribe" @modal-closed="handleCloseModal">
        <template #content>
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="left">
                        <h3>Get up to</h3>
                        <h2>{{ blok.discount }}% OFF</h2>
                    </div>
                    <div class="main">
                        <div class="image">
                            <NuxtImg
                                v-if="blok.image?.filename"
                                :src="blok.image?.filename"
                                :alt="blok.image?.alt ?? undefined"
                                provider="storyblok"
                                format="webp"
                                densities="x1 x2"
                                loading="lazy"
                            />
                        </div>

                        <p id="subscribe-modal-message">{{ blok.message }}</p>

                        <form novalidate @submit.prevent="submitForm">
                            <div class="input-group">
                                <FormInput
                                    v-model="formData.email"
                                    placeholder="Email address"
                                    name="email"
                                    type="email"
                                    :error-message="emailError ?? undefined"
                                    :aria-label="blok.message ? undefined : 'Email address'"
                                    :aria-labelledby="blok.message ? 'subscribe-modal-message' : undefined"
                                    @input="onEmailInput"
                                ></FormInput>

                                <!-- Typo suggestion -->
                                <div v-if="suggestedEmail && !formState.success" class="error-message">
                                    <span class="underline cursor-pointer" @click.stop="applyTypoSuggestion">
                                        {{ t('forms.emailTypoSuggestion', { email: suggestedEmail }) }}
                                    </span>
                                </div>

                                <!-- Role-based nudge -->
                                <div v-else-if="roleNudgeMessage && !formState.success" class="error-message">
                                    <span>{{ roleNudgeMessage }}</span>
                                </div>
                            </div>
                            <div class="button-group">
                                <button type="submit" name="Submit" :disabled="shouldDisableButton">Join Now</button>

                                <div v-if="formState.success" class="success">
                                    Thank you, you’re now able to use discount code:
                                    <span>{{ blok.discountCode }}</span>
                                </div>
                                <div v-else-if="formState.isSubmitted && !formState.success" class="response-error">
                                    {{ formState.responseMsg }}
                                </div>
                                <button type="button" class="close" @click.prevent="handleCloseModal">
                                    <span v-if="!formState.success">NO THANKS</span>
                                    <span v-else>CLOSE</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import Modal from './Modal.vue';
import { useModalStore } from '@nuxt-monorepo/base/app/store/modal';
import type {
    ISubscribeModal,
    ISubscribeModalFormState,
} from '@nuxt-monorepo/ui/types/components/modals/SubscribeModal';
import { useEmailFieldValidation } from '@nuxt-monorepo/ui/composables/useEmailFieldValidation';
import FormInput from '@nuxt-monorepo/ui/components/Form/Input.vue';

const modalStore: any = useModalStore();
const { t } = useI18n();

const { blok, formState } = defineProps<{ blok: ISubscribeModal; formState: ISubscribeModalFormState }>();

const emit = defineEmits<{
    (e: 'submit', formData: any): void;
    (e: 'reset'): void;
}>();

const formData = ref<{ email: string }>({
    email: '',
});

const email = computed<string>({
    get: () => formData.value.email,
    set: (value: string) => {
        formData.value.email = value;
    },
});

const {
    v$,
    emailError,
    suggestedEmail,
    roleNudgeMessage,
    shouldDisableButton,
    onInput,
    applyTypoSuggestion,
    validateBeforeSubmit,
    onSubmitSuccess,
} = useEmailFieldValidation({
    email,
    t,
});

const eraseFormData = () => {
    formData.value.email = '';
    v$.$reset();
};

const handleCloseModal = () => {
    modalStore.toggleModal('Subscribe', false);
    eraseFormData();
};

const onEmailInput = () => {
    onInput();
};

const submitForm = async () => {
    // Validate and check if can submit
    if (!validateBeforeSubmit()) {
        return;
    }

    emit('submit', formData.value);
};

// Watch for success to reset form
watch(
    () => formState.success,
    (success) => {
        if (success) {
            formData.value.email = '';
            onSubmitSuccess();
        }
    }
);
</script>

<style lang="postcss">
.subscribe-modal {
    .background {
        @apply bg-gray-900/30;
    }
    .margin-content {
        @apply px-8 shadow-none;
    }
    .modal-dialog {
        @apply mx-auto;
    }
    .modal-wrapper {
        @apply max-w-[700px] md:w-auto w-full md:h-auto mt-32;

        .modal-content {
            @apply flex flex-row md:h-auto p-0 w-full md:w-auto bg-transparent;

            .left {
                @apply flex-[0] hidden md:flex flex-col justify-center items-center bg-primary text-center text-white px-8;

                h2 {
                    @apply text-5xl font-bold;
                }

                h3 {
                    @apply text-2xl font-semibold whitespace-nowrap;
                }
            }

            .main {
                @apply pt-4 pb-2 px-16 flex-1 flex flex-col justify-between items-center bg-white text-center;

                .image {
                    @apply mt-4 mb-8;
                }

                p {
                    @apply text-base text-gray-500;
                }

                form {
                    @apply w-full mt-8;
                }

                .input-group {
                    input {
                        @apply w-full px-2 py-2 border border-gray-300 rounded;
                        &::placeholder {
                            @apply text-gray-500;
                        }
                    }
                    .error-message {
                        @apply mt-1 text-error text-sm;
                    }
                }

                .success,
                .response-error {
                    @apply mt-2;
                    span {
                        @apply font-semibold;
                    }
                }
                .success {
                    @apply text-green-700;
                }
                .response-error {
                    @apply text-error;
                }

                .button-group {
                    @apply flex flex-col mt-2;
                    button {
                        @apply mt-2 py-2 px-4 rounded text-gray-500 text-xs;
                        &[type='submit'] {
                            @apply bg-secondary/80 text-white text-base transition-all;
                            &:hover:not(:disabled) {
                                @apply bg-secondary/100;
                            }
                            &:disabled {
                                @apply opacity-85 cursor-not-allowed;
                            }
                        }
                    }
                }
            }
        }
    }
}
</style>
